import { useEffect, useState } from "react";
import {
  GAME_INVITATION_FREQUENCY,
  GAME_PAYMENT_MODE,
  GAME_VISIBILITY,
  PLAYERS_TEAM_SIZE,
  SPORTS,
} from "../../../constants/game";
import { useToast } from "../../../contexts/ToastContext";
import { useLocalStorage } from "../../../hooks/useLocalStorage";
import apiService from "../../../services/api";
import {
  getPlainTextFromHtml,
  isDateTimeValid,
  isLocationValid,
  isPlayerDetailsValid,
  isRefundCutoffValid,
  isSportVisibilityValid,
  validateRevolutLink,
} from "./gameFormValidation";

export const useGameFormState = ({ isEdit, gameId }) => {
  const { showToast } = useToast();

  const { storedValue: savedGameData, setValue: saveGameData } =
    useLocalStorage("lastGameData", null);

  const [selectedGroup, setSelectedGroup] = useState(null);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(!!isEdit);
  const [isGroupCreating, setIsGroupCreating] = useState(false);

  const [existingGroups, setExistingGroups] = useState([]);
  const [fetchingGroups, setFetchingGroups] = useState(false);

  const [originalGame, setOriginalGame] = useState(null);

  const [groupData, setGroupData] = useState({
    name: "",
    country: "",
    city: "",
    description: "",
    image: null,
  });

  const [gameData, setGameData] = useState({
    visibility: GAME_VISIBILITY.PUBLIC,
    sport: SPORTS[0],
    description: "",
    date: "",
    time: "",
    duration_minutes: 60,
    venue: "",
    location: {
      address: "",
      lat: "",
      lng: "",
    },
    how_to_find: "",
    players: PLAYERS_TEAM_SIZE.FIVE_V_FIVE,
    whatsapp_link: "",
    payment_mode: "",
    invitation_frequency: GAME_INVITATION_FREQUENCY.NO_INVITE,
    recurring: null,
    playerAmount: "2.50",
    totalAmount: "3.30",
    use_revolut: false,
    revolut_link: "",
    refund_policy: "NO_REFUNDS",
    refund_cutoff_hours: 0,
  });

  // Prefill from the host's last created game, create mode only
  useEffect(() => {
    if (!isEdit && savedGameData) {
      setGameData((prev) => ({
        ...prev,
        ...savedGameData,
      }));
    }
  }, [isEdit, savedGameData]);

  // Existing groups are only needed for the create wizard's group step
  useEffect(() => {
    if (isEdit) return;

    const fetchExistingGroups = async () => {
      setFetchingGroups(true);
      try {
        const response = await apiService.fetchUserGroups({ owner_only: true });
        if (response.success) {
          setExistingGroups(response.data);
        }
      } catch (error) {
        showToast(error.error, "error");
        setExistingGroups([]);
      } finally {
        setFetchingGroups(false);
      }
    };

    fetchExistingGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit]);

  // Fetch game data for edit mode
  useEffect(() => {
    const getGame = async () => {
      if (!isEdit || !gameId) return;

      try {
        setIsFetching(true);
        const response = await apiService.fetchGameById(gameId);

        if (response.success) {
          const game = response.data;
          setOriginalGame(game);

          const gameDateTime = new Date(game.datetime);
          const date = gameDateTime.toISOString().split("T")[0];
          const time = gameDateTime.toTimeString().slice(0, 5);

          let totalAmount = "3.30";
          let playerAmount = "2.50";

          if (game.payment_mode === GAME_PAYMENT_MODE.IN_APP) {
            totalAmount = game.amount_per_player;
            const total = parseFloat(game.amount_per_player);
            const playerWith20Percent = total / 1.2;
            const feeWith20Percent = playerWith20Percent * 0.2;

            if (feeWith20Percent >= 0.8) {
              playerAmount = playerWith20Percent.toFixed(2);
            } else {
              playerAmount = (total - 0.8).toFixed(2);
            }
          } else {
            playerAmount = game.amount_per_player;
            const fee = Math.max(0.8, parseFloat(playerAmount) * 0.2);
            totalAmount = (parseFloat(playerAmount) + fee).toFixed(2);
          }

          setGameData({
            visibility: game.visibility || GAME_VISIBILITY.PUBLIC,
            sport: game.sport || SPORTS[0],
            date,
            time,
            duration_minutes: game.duration_minutes || 60,
            description: game.description || "",
            venue: game.venue || "",
            location: game.location || {
              address: "",
              lat: "",
              lng: "",
            },
            how_to_find: game.how_to_find || "",
            players: game.players || PLAYERS_TEAM_SIZE.FIVE_V_FIVE,
            whatsapp_link: game.whatsapp_link || "",
            payment_mode: game.payment_mode || GAME_PAYMENT_MODE.IN_APP,
            invitation_frequency:
              game.invitation_frequency || GAME_INVITATION_FREQUENCY.NO_INVITE,
            recurring: game.recurring || false,
            playerAmount,
            totalAmount,
            use_revolut: game.use_revolut || false,
            revolut_link: game.revolut_link || "",
            refund_policy: game.refund_policy || "NO_REFUNDS",
            refund_cutoff_hours: game.refund_cutoff_hours || 0,
          });

          if (game.group_id) {
            setSelectedGroup({ id: game.group_id });
          }
        }
      } catch (error) {
        showToast(error.error, "error");
      } finally {
        setIsFetching(false);
      }
    };

    getGame();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, gameId]);

  const handleGroupInputChange = (field) => (event) => {
    setGroupData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleImageChange = (file) => {
    setGroupData((prev) => ({
      ...prev,
      image: file,
    }));
  };

  const handleGameInputChange = (field) => (event) => {
    setGameData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleGameTypeChange = (event) => {
    setGameData((prev) => ({
      ...prev,
      visibility: event.target.value,
    }));
  };

  const handleSportChange = (value) => {
    setGameData((prev) => ({
      ...prev,
      sport: value,
    }));
  };

  const handleLocationSelect = (location) => {
    setGameData((prev) => ({
      ...prev,
      location: {
        ...prev.location,
        place_id: location.placeId,
        lat: location.lat,
        lng: location.lng,
      },
    }));
  };

  const handleAddressChange = (address) => {
    setGameData((prev) => ({
      ...prev,
      location: { ...prev.location, address },
    }));
  };

  const handleVenueChange = (venue) => {
    setGameData((prev) => ({
      ...prev,
      venue,
    }));
  };

  const handlePlayerAmountChange = (event) => {
    const amount = event.target.value;
    const fee = Math.max(0.8, parseFloat(amount) * 0.2);
    const total = amount ? (parseFloat(amount) + fee).toFixed(2) : "";

    setGameData((prev) => ({
      ...prev,
      playerAmount: amount,
      totalAmount: total,
    }));
  };

  const handleRevolutToggle = (event) => {
    setGameData((prev) => ({
      ...prev,
      use_revolut: event.target.checked,
    }));
  };

  const handleRevolutLinkChange = (event) => {
    setGameData((prev) => ({
      ...prev,
      revolut_link: event.target.value,
    }));
  };

  const handleSubmitGame = async (e, groupIdOverride) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isSportVisibilityValid(gameData)) {
      showToast("Please select a sport and visibility", "error");
      return;
    }
    if (!isDateTimeValid(gameData)) {
      if (!gameData.date) {
        showToast("Please select a date for the game", "error");
        return;
      }
      if (!gameData.time) {
        showToast("Please select a time for the game", "error");
        return;
      }
      showToast(
        "Cannot create game in the past. Please select a future date and time",
        "error",
      );
      return;
    }
    if (!isLocationValid(gameData)) {
      if (!gameData.venue.trim()) {
        showToast("Please enter a venue for the game", "error");
        return;
      }
      showToast("Please select a location on the map", "error");
      return;
    }
    if (!isPlayerDetailsValid(gameData)) {
      showToast("Please select maximum number of players", "error");
      return;
    }
    if (gameData.use_revolut) {
      const revolutError = validateRevolutLink(gameData.revolut_link);
      if (revolutError) {
        showToast(revolutError, "error");
        return;
      }
    }
    if (!isRefundCutoffValid(gameData)) {
      showToast(
        "Please enter a custom refund cut-off time between 1 and 168 hours",
        "error",
      );
      return;
    }

    const groupId = groupIdOverride ?? selectedGroup?.id;
    if (!groupId) {
      showToast("Please select or create a group for this game", "error");
      return;
    }

    const datetime = new Date(`${gameData.date}T${gameData.time}`).getTime();
    const gamePayload = {
      visibility: gameData.visibility,
      sport: gameData.sport,
      description: getPlainTextFromHtml(gameData.description)
        ? gameData.description
        : undefined,
      datetime,
      duration_minutes: gameData.duration_minutes,
      recurring: gameData.recurring,
      invitation_frequency: gameData.invitation_frequency,
      venue: gameData.venue,
      location: gameData.location,
      how_to_find: gameData.how_to_find,
      players: gameData.players,
      whatsapp_link: gameData.whatsapp_link,
      payment_mode: gameData.payment_mode,
      amount_per_player:
        gameData.payment_mode === GAME_PAYMENT_MODE.IN_APP
          ? gameData.totalAmount
          : gameData.playerAmount,
      group_id: groupId,
      use_revolut: gameData.use_revolut,
      revolut_link: gameData.revolut_link,
      refund_policy: gameData.refund_policy,
      refund_cutoff_hours: Number(gameData.refund_cutoff_hours) || 0,
    };

    setIsLoading(true);
    try {
      let response;

      if (isEdit && gameId) {
        response = await apiService.updateGame(gameId, gamePayload);
      } else {
        response = await apiService.createGame(gamePayload);
      }

      if (response.success) {
        const successMessage = isEdit
          ? response.message || "Game updated successfully!"
          : response.message || "Game created successfully!";
        showToast(successMessage, "success");

        if (!isEdit) {
          const dataToSave = {
            visibility: gameData.visibility,
            sport: gameData.sport,
            description: gameData.description,
            venue: gameData.venue,
            location: gameData.location,
            how_to_find: gameData.how_to_find,
            players: gameData.players,
            whatsapp_link: gameData.whatsapp_link,
            payment_mode: gameData.payment_mode,
            invitation_frequency: gameData.invitation_frequency,
            recurring: gameData.recurring,
            playerAmount: gameData.playerAmount,
            totalAmount: gameData.totalAmount,
            use_revolut: gameData.use_revolut,
            revolut_link: gameData.revolut_link,
            refund_policy: gameData.refund_policy,
            refund_cutoff_hours: gameData.refund_cutoff_hours,
            time: gameData.time,
            duration_minutes: gameData.duration_minutes,
          };
          saveGameData(dataToSave);
        }

        return { success: true, data: response.data };
      }

      const errorMessage = isEdit
        ? response?.error || "Failed to update game"
        : response?.error || "Failed to create game";
      showToast(errorMessage, "error");
      return { success: false };
    } catch (error) {
      const errorMessage = isEdit
        ? error?.details?.[0] || error?.error || "Failed to update game"
        : error?.details?.[0] || error?.error || "Failed to create game";
      showToast(errorMessage, "error");
      return { success: false };
    } finally {
      setIsLoading(false);
    }
  };

  const createGroupAPI = async () => {
    try {
      setIsGroupCreating(true);

      if (!groupData.name.trim()) {
        showToast("Please enter a group name", "error");
        return null;
      }
      if (!groupData.country) {
        showToast("Please select a country", "error");
        return null;
      }
      if (!groupData.city) {
        showToast("Please select a city", "error");
        return null;
      }

      const formData = new FormData();
      formData.append("name", groupData.name);
      formData.append("country", groupData.country);
      formData.append("city", groupData.city);
      formData.append("description", groupData.description);

      if (groupData.image) {
        formData.append("avatar", groupData.image);
      }

      const response = await apiService.createGroupWithAvatar(formData);

      setSelectedGroup(response.data);
      setShowCreateGroup(false);
      showToast(response.message || "Group created successfully!", "success");

      return response.data;
    } catch (error) {
      showToast(error.message || "Failed to create group", "error");
      return null;
    } finally {
      setIsGroupCreating(false);
    }
  };

  const handleSelectGroup = (group) => {
    if (selectedGroup === group) {
      setSelectedGroup(null);
    } else {
      setSelectedGroup(group);
    }
    setShowCreateGroup(false);
  };

  const handleCreateNewGroup = () => {
    setShowCreateGroup(true);
    setSelectedGroup(null);
  };

  const isGroupSelected = (group) => selectedGroup === group;

  return {
    gameData,
    setGameData,
    groupData,
    selectedGroup,
    setSelectedGroup,
    showCreateGroup,
    setShowCreateGroup,
    existingGroups,
    fetchingGroups,
    isGroupCreating,
    isLoading,
    isFetching,
    originalGame,
    handleGroupInputChange,
    handleImageChange,
    handleGameInputChange,
    handleGameTypeChange,
    handleSportChange,
    handleLocationSelect,
    handleAddressChange,
    handleVenueChange,
    handlePlayerAmountChange,
    handleRevolutToggle,
    handleRevolutLinkChange,
    handleSubmitGame,
    createGroupAPI,
    handleSelectGroup,
    handleCreateNewGroup,
    isGroupSelected,
  };
};
